import { isToday } from "date-fns";
import { AffectedTrain as AffectedTrainType } from "features/CenterContent/RoleContent/Vaktleder/types";
import DashedLine from "features/CenterContent/VehicleDetails/shared/DashedLine";
import { TrainInfoState } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import { AffectedTrainGrid } from "features/VehicleSidebar/AffectedTrains/styles";
import { makeTimeDisplay } from "features/VehicleSidebar/AffectedTrains/utils/timeFormatting";
import {
  getEventSemantics,
  getInfoTitle,
} from "features/VehicleSidebar/AffectedTrains/utils/utils";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  DropsBadge,
  Field,
  HStack,
  Text,
  VStack,
} from "shared/components/Spor";
import { TextTooltip } from "shared/components/TextTooltip";
import { InfrastructureIncidentIcon18 } from "shared/icons/InfrastructureIncidentIcon18";
import { formatDateString } from "shared/utils/dateTimeUtils";

type AffectedTrainProps = {
  train: AffectedTrainType;
  selectedTrains: AffectedTrainType[];
  updateSelectedTrains: (train: AffectedTrainType) => void;
  isPending: boolean;
  currentTime: Date;
};

const AffectedTrain: FC<AffectedTrainProps> = ({
  train,
  selectedTrains,
  updateSelectedTrains,
  isPending,
  currentTime,
}) => {
  const { t } = useTranslation();
  const trainDateIsToday = isToday(new Date(train.trainId.nominalDate));
  const additionalInfo = train.otherOperationalTrainInformation.at(0);
  const newestVersion = additionalInfo?.versions.at(0);
  const newestVersionopen = newestVersion?.state === TrainInfoState.OPEN;

  const inSelectedBatch = selectedTrains.some(
    ({ trainId, incidentId }) =>
      train.trainId.identifier === trainId.identifier &&
      train.incidentId === incidentId &&
      train.trainId.nominalDate === trainId.nominalDate,
  );

  const matchSelectedIncidentId =
    selectedTrains.length > 0
      ? train.incidentId === selectedTrains[0].incidentId
      : true;

  const timeDisplay = makeTimeDisplay(train, currentTime, t);

  return (
    <>
      <AffectedTrainGrid>
        <TextTooltip
          showArrow
          placement="right"
          label={t("Ikke fra samme brudd som valgte tog")}
          isDisabled={matchSelectedIncidentId}
        >
          <Field>
            <Checkbox
              mr="18px"
              gridColumn="1"
              opacity={matchSelectedIncidentId ? 1 : 0.15}
              disabled={isPending || !matchSelectedIncidentId}
              checked={inSelectedBatch}
              onCheckedChange={() => {
                updateSelectedTrains(train);
              }}
            />
          </Field>
        </TextTooltip>
        <VStack gap="3px" alignItems="flex-start" width="100%">
          <HStack gap="6px" width="100%">
            <Text fontWeight="bold" mt={0} mb="auto">
              {train.trainId.identifier}
            </Text>
            <HStack id="incident" ml="auto" alignItems="flex-start" mr={0}>
              <InfrastructureIncidentIcon18 />
              <Text
                maxWidth="200px"
                style={{
                  // Behaviour: Use max 3 lines and show "..." at overflow
                  display: "-webkit-box",
                  WebkitLineClamp: "3",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {isPending ? t("Behandles...") : train.eventStretchName}
              </Text>
            </HStack>
          </HStack>
          <HStack gap="6px" width="100%">
            <Text color="text.highlight">{timeDisplay}</Text>
            {newestVersionopen && additionalInfo && (
              <HStack id="incident" ml="auto" alignItems="flex-start" mr={0}>
                <DropsBadge
                  semantics={getEventSemantics(newestVersion.type)}
                  key={additionalInfo.latestInfoUuid}
                  height="fit-content"
                  width="fit-content"
                  whiteSpace="nowrap"
                >
                  {getInfoTitle(newestVersion, t)}
                </DropsBadge>
              </HStack>
            )}
          </HStack>
          <Text>
            {train.origin}–{train.destination}
          </Text>

          {!trainDateIsToday && (
            <Text color="text.highlight">
              {formatDateString(train.trainId.nominalDate, "dd.MM.yy")}
            </Text>
          )}
        </VStack>
      </AffectedTrainGrid>
      <DashedLine variant="secondary" />
    </>
  );
};

export default AffectedTrain;
